import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { Close } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  FormLabel,
  IconButton,
  OutlinedInput,
  Typography as T,
} from "@mui/material";
import useDataset from "datasets/hooks/useDataset";
import useDatasetReportFQNs from "datasets/pages/dashboards/hooks/useDatasetReportFQNs";
import useDatasetReports from "datasets/pages/dashboards/hooks/useDatasetReports";
import Report from "datasets/pages/dashboards/types/interfaces/report";
import { v4 as uuidv4 } from "uuid";
import yn from "yn";
import rumClient, { trackEvent } from "~/utils/monitoring";

const { VITE_CATALOG_API, VITE_SIGMA_URL, VITE_SIGMA_SELF_SERVICE_ENABLED } = import.meta.env;

interface FormValues {
  displayName: string;
  description: string;
  sourceUrl?: string;
}

interface ModalBodyProps {
  onClose: () => void;
  reportFQNs?: string[];
  report?: Report;
}

const ModalBody = ({ onClose, reportFQNs = [], report }: ModalBodyProps) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const { mutate: mutateDataset } = useDataset();
  const { mutate: mutateReportFQNs } = useDatasetReportFQNs();
  const { mutate: mutateReports } = useDatasetReports();
  const sigmaSelfServeEnabled = yn(VITE_SIGMA_SELF_SERVICE_ENABLED);

  const { datasetId } = useParams();

  const [formError, setFormError] = useState<string>();

  const form = useForm<FormValues>({
    mode: "onBlur",
    defaultValues: {
      displayName: report?.displayName ?? "",
      description: report?.description ?? "",
      sourceUrl: report?.sourceUrl ?? "",
    },
  });

  const { isValid, isDirty, dirtyFields } = form.formState;

  const refreshData = async () => {
    await mutateDataset();
    await mutateReportFQNs();
    await mutateReports();
  };

  const handleCreate = async (data: FormValues) => {
    try {
      const resp = await fetch(`${VITE_CATALOG_API}/reports`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: `${data.displayName}-${uuidv4()}`,
          displayName: data.displayName,
          description: data.description,
          sourceUrl: data.sourceUrl || undefined,
        }),
      });

      const reportResp = await resp.json();
      if (!resp.ok) {
        throw Error(reportResp.detail);
      }

      const updatedReports = {
        reports: [...reportFQNs, reportResp.fullyQualifiedName],
      };

      const datasetResp = await fetch(`${VITE_CATALOG_API}/datasets/${datasetId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updatedReports),
      });

      const datasetRespData = await datasetResp.json();
      if (!datasetResp.ok) {
        throw Error(datasetRespData.detail);
      }

      trackEvent("dataset_dashboard_created", {
        dashboard_id: reportResp.id,
        dataset_id: datasetId,
      });

      navigate(reportResp.id);
      await refreshData();
    } catch (e: any) {
      console.error(e);
      rumClient?.recordError(e);
      setFormError(e.message || "Invalid dashboard url");
    }
  };

  const handleUpdate = async (data: FormValues) => {
    const body: Partial<FormValues> = Object.keys(dirtyFields).reduce((acc, key) => {
      const field = key as keyof FormValues;

      if (dirtyFields[field]) {
        acc[field] = data[field];
      }
      return acc;
    }, {} as Partial<FormValues>);

    // If nothing changed, bail out early
    if (Object.keys(body).length === 0) {
      setLoading(false);
      return;
    }

    try {
      const resp = await fetch(`${VITE_CATALOG_API}/reports/id/${report?.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      const respData = await resp.json();
      if (!resp.ok) {
        throw Error(respData.detail);
      }

      trackEvent("dataset_dashboard_edited", {
        dashboard_id: report?.id,
        dataset_id: datasetId,
      });

      onClose();
      await refreshData();
    } catch (e: any) {
      console.error(e);
      rumClient?.recordError(e);
      setFormError(e.message || "Invalid dashboard url");
    }
  };

  const onSubmit = async (data: FormValues) => {
    setLoading(true);

    if (report) {
      await handleUpdate(data);
    } else {
      await handleCreate(data);
    }

    setLoading(false);
  };

  const onFormChange = () => {
    // This is used exclusively to clear formErrors to force the users to change some information that we know needs to be changed
    setFormError(undefined);
  };

  return (
    <form onChange={onFormChange} onSubmit={form.handleSubmit(onSubmit)}>
      <DialogTitle sx={{ p: 3 }}>{report ? "Edit" : "New"} Dashboard</DialogTitle>
      <IconButton sx={{ position: "absolute", right: 16, top: 20 }} onClick={onClose}>
        <Close />
      </IconButton>
      <DialogContent sx={{ pt: 0, pb: 3, gap: 2 }}>
        <FormControl fullWidth>
          <Controller
            control={form.control}
            name="displayName"
            rules={{ required: { value: true, message: "Required" } }}
            render={({ field, fieldState: { error } }) => (
              <>
                <FormLabel htmlFor={field.name}>
                  <T variant="label" sx={{ opacity: 1 }}>
                    Name *
                  </T>
                </FormLabel>
                <OutlinedInput
                  {...field}
                  autoComplete="off"
                  autoFocus
                  id={field.name}
                  placeholder="Enter dashboard name"
                  size="small"
                  error={!!error}
                />
                {error ? <FormHelperText error>{error.message}</FormHelperText> : null}
              </>
            )}
          />
        </FormControl>
        <FormControl fullWidth>
          <Controller
            control={form.control}
            name="description"
            render={({ field }) => (
              <>
                <FormLabel htmlFor={field.name}>
                  <T variant="label" sx={{ opacity: 1 }}>
                    Description
                  </T>
                </FormLabel>
                <OutlinedInput
                  {...field}
                  autoComplete="off"
                  id={field.name}
                  multiline
                  rows={3}
                  placeholder="Enter dashboard description"
                  size="small"
                />
              </>
            )}
          />
        </FormControl>
        {!report && (
          <FormControl fullWidth>
            <Controller
              control={form.control}
              name="sourceUrl"
              rules={{
                required: !sigmaSelfServeEnabled ? { value: true, message: "Required" } : false,
                validate: {
                  validUrl: (value?: string) => {
                    if (formError) {
                      return false;
                    }

                    if (!value) {
                      return true;
                    }

                    try {
                      const url = new URL(value);
                      const isWorkbookUrl =
                        url.origin === VITE_SIGMA_URL &&
                        url.pathname.startsWith("/manifold/workbook/");
                      return isWorkbookUrl || "Invalid URL";
                    } catch (error) {
                      return "Invalid URL";
                    }
                  },
                },
              }}
              render={({ field, fieldState: { error } }) => {
                const hasError = !!error || !!formError;

                return (
                  <>
                    <FormLabel htmlFor={field.name}>
                      <T variant="label" sx={{ opacity: 1 }}>
                        Source URL {!sigmaSelfServeEnabled && "*"}
                      </T>
                    </FormLabel>
                    <OutlinedInput
                      {...field}
                      autoComplete="off"
                      id={field.name}
                      placeholder="Enter dashboard link"
                      size="small"
                      error={hasError}
                    />
                    <FormHelperText error={hasError}>
                      {formError ||
                        error?.message ||
                        (sigmaSelfServeEnabled ? (
                          <span style={{ opacity: 0.7 }}>
                            If left empty, a new blank dashboard will be created for you
                          </span>
                        ) : (
                          " "
                        ))}
                    </FormHelperText>
                  </>
                );
              }}
            />
          </FormControl>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          color="secondary"
          onClick={onClose}
          sx={{ bgcolor: "grey.100", minWidth: 70, px: 1.25, py: 1 }}
        >
          Cancel
        </Button>
        <LoadingButton
          type="submit"
          disabled={!isDirty || !isValid || !!formError}
          loading={loading}
          variant="contained"
          sx={{ minWidth: 70, px: 1.25, py: 1 }}
        >
          {report ? "Save" : "Add"}
        </LoadingButton>
      </DialogActions>
    </form>
  );
};

interface AddReportModalProps {
  open: boolean;
  onClose: () => void;
  datasetId?: string;
  reportFQNs?: string[];
  report?: Report;
}

const AddReportModal = ({ open, onClose, reportFQNs = [], report }: AddReportModalProps) => {
  return (
    <Dialog
      fullWidth
      open={open}
      onClose={onClose}
      /* Workaround to handle autoFocus in dev mode */
      disableRestoreFocus={import.meta.env.DEV}
    >
      {open && <ModalBody onClose={onClose} reportFQNs={reportFQNs} report={report} />}
    </Dialog>
  );
};

export default AddReportModal;
